import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { isResponsiveRoute } from "./responsive-routes";

/**
 * `<meta name="viewport">` viene comentado en index.html desde el commit
 * inicial, así que la app renderiza con el viewport de layout por defecto
 * (~980px): el dashboard legacy —pensado solo para escritorio— se ve completo y
 * se navega con pinch-zoom en el teléfono.
 *
 * Las pantallas de autenticación sí son responsive, pero con 980px ninguna de
 * sus media queries llega a dispararse. Este servicio activa el viewport real
 * únicamente mientras se está en esas rutas y lo devuelve a 980px al salir, de
 * modo que el resto de la app se sigue viendo exactamente como hoy.
 *
 * Cuando el legacy se vuelva responsive, esto se borra y se descomenta la
 * etiqueta en index.html.
 */
const AUTH_VIEWPORT = 'width=device-width, initial-scale=1';

// Ancho de layout que los navegadores asumen cuando no hay etiqueta viewport.
// Hay que fijarlo explícitamente: quitar la etiqueta NO revierte el viewport,
// el navegador conserva el último valor aplicado.
const LEGACY_VIEWPORT = 'width=980';

// Qué rutas son responsive vive en `responsive-routes.ts` (una sola lista, con
// su copia en línea en index.html vigilada por el lint).

@Injectable({ providedIn: 'root' })
export class AuthViewportService {

  constructor(private router: Router) { }

  /** Se llama una vez al arrancar la app. */
  watch() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.apply(event.urlAfterRedirects));
  }

  private apply(url: string) {
    const route = url.split(/[?#]/)[0].replace(/^\/+/, '');
    const responsive = isResponsiveRoute(route);
    this.tag().content = responsive ? AUTH_VIEWPORT : LEGACY_VIEWPORT;
    // Gancho para acotar los puentes sobre piezas heredadas (`dxf-w-*`, la
    // fuente de 16 de los campos, los modales) a las rutas verificadas: una
    // ventana de escritorio estrechada en una ruta no migrada no los recibe.
    document.documentElement.dataset.viewport = responsive ? 'device' : 'legacy';
  }

  private tag() {
    let meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');

    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'viewport';
      document.head.appendChild(meta);
    }

    return meta;
  }
}
